import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';

import { z } from 'zod';
import { toast } from 'sonner';
import { toNoonUTC } from '@/shared/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { getIntroIntakeFormApiEndPoint } from '@shared';
import { shortFormIntakeApi } from '@client/api/waterlily';
import { quickPlanPersonSchema, type QuickPlanPersonValues } from '@/features/client-quick-plan';

export const intakeFormQuickPlanSchema = quickPlanPersonSchema
  .extend({
    email: z.string().email('Invalid email address'),
    partnerEmail: z.string().default(''),
    hasPartner: z.boolean().default(false),
    partner: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.hasPartner) {
      if (!data.partner) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Partner details are required',
          path: ['partner'],
        });
      } else {
        const result = quickPlanPersonSchema.safeParse(data.partner);
        if (!result.success) {
          for (const issue of result.error.issues) {
            ctx.addIssue({ ...issue, path: ['partner', ...issue.path] });
          }
        }
      }
      if (!data.partnerEmail) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Partner email is required',
          path: ['partnerEmail'],
        });
      } else if (!z.string().email().safeParse(data.partnerEmail).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid email address',
          path: ['partnerEmail'],
        });
      }
    }
  });

export type IntakeFormQuickPlanFormValues = z.infer<typeof intakeFormQuickPlanSchema>;

interface UseIntakeFormQuickPlanOptions {
  advisorId: string;
  onSuccess?: (data: ShortFormIntakeResponse) => void;
}

const PARTNER_ELIGIBLE_STATUSES = ['Married', 'Partnered'];

function mapPersonToRequest(
  formValues: QuickPlanPersonValues,
  advisorId: string,
  email?: string,
): ShortFormIntakeRequest {
  const genderMap: Record<string, string> = {
    male: 'Male',
    female: 'Female',
  };

  const smokerMap: Record<string, string> = {
    never: 'No',
    former: 'Yes',
    current: 'Yes',
  };

  const intakeFormUrl = getIntroIntakeFormApiEndPoint(advisorId, false);

  return {
    clientFirstName: formValues.firstName,
    clientLastName: formValues.lastName,
    clientBirthDate: toNoonUTC(formValues.birthDate),
    clientGenderAtBirth: genderMap[formValues.sex] ?? 'Male',
    clientSmokerEver: smokerMap[formValues.smokingStatus] ?? 'No',
    clientMaritalStatus: formValues.maritalStatus,
    clientZipCode: formValues.zipCode,
    ...(email ? { clientEmail: email, skipInviteEmail: false } : {}),
    intakeFormUrl,
  };
}

export function useIntakeFormQuickPlan({ advisorId, onSuccess }: UseIntakeFormQuickPlanOptions) {
  const navigate = useNavigate();
  const lastCoupleLinked = useRef(true);
  const lastCoupleDeliveryFailed = useRef(false);

  const form = useForm<IntakeFormQuickPlanFormValues>({
    resolver: zodResolver(intakeFormQuickPlanSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      zipCode: '',
      maritalStatus: '',
      email: '',
      partnerEmail: '',
      hasPartner: false,
    },
  });

  const hasPartner = form.watch('hasPartner');
  const maritalStatus = form.watch('maritalStatus');
  const canAddPartner = PARTNER_ELIGIBLE_STATUSES.includes(maritalStatus);
  const partnerLabel = maritalStatus === 'Married' ? 'spouse' : 'partner';

  function clearPartner() {
    const { partner, ...values } = form.getValues();
    form.reset({ ...values, hasPartner: false, partnerEmail: '' }, { keepDirty: true, keepTouched: true });
  }

  function handleTogglePartner(checked: boolean) {
    if (checked) {
      form.setValue('hasPartner', true, { shouldValidate: true });
      form.setValue('partner.maritalStatus', maritalStatus, { shouldValidate: true });
    } else {
      clearPartner();
    }
  }

  function handleMaritalStatusChange(value: string) {
    form.setValue('maritalStatus', value, { shouldValidate: true });
    if (!PARTNER_ELIGIBLE_STATUSES.includes(value) && hasPartner) {
      clearPartner();
    } else if (hasPartner) {
      form.setValue('partner.maritalStatus', value, { shouldValidate: true });
    }
  }

  function handlePartnerMaritalStatusChange(value: string) {
    form.setValue('partner.maritalStatus', value, { shouldValidate: true });
    form.setValue('maritalStatus', value, { shouldValidate: true });
    if (!PARTNER_ELIGIBLE_STATUSES.includes(value)) {
      clearPartner();
    }
  }

  const mutation = useMutation({
    mutationFn: async (
      formValues: IntakeFormQuickPlanFormValues,
    ): Promise<ShortFormIntakeResponse> => {
      const primaryRequest = mapPersonToRequest(formValues, advisorId, formValues.email);

      if (formValues.hasPartner && formValues.partner) {
        const partnerRequest = mapPersonToRequest(
          formValues.partner,
          advisorId,
          formValues.partnerEmail,
        );
        const coupleResponse = await shortFormIntakeApi.createCouple({
          primary: primaryRequest,
          partner: partnerRequest,
        });
        lastCoupleLinked.current = coupleResponse.linked;
        lastCoupleDeliveryFailed.current = [coupleResponse.primary, coupleResponse.partner].some(
          result => result.intakeFormDeliveryStatus === 'failed',
        );
        return coupleResponse.primary;
      }

      return shortFormIntakeApi.create(primaryRequest);
    },
    onSuccess: (data, variables) => {
      const intakeFormDeliveryFailed = variables.hasPartner
        ? lastCoupleDeliveryFailed.current
        : data.intakeFormDeliveryStatus === 'failed';

      if (intakeFormDeliveryFailed) {
        toast.error('We couldn’t send that email', {
          description: 'Nothing reached your client. Try sending it again.',
        });
      } else if (variables.hasPartner) {
        if (lastCoupleLinked.current) {
          toast.success('Plans created and intake form sent for both clients');
        } else {
          toast.warning(
            'Plans created and intake form sent, but partner linking failed. Please link them manually from your dashboard.',
          );
        }
      } else {
        toast.success('Plan created and intake form sent');
      }

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate(`/advisors/${advisorId}/clients`);
      }
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create plan. Please try again.';
      toast.error(errorMessage);
    },
  });

  return {
    form,
    hasPartner,
    canAddPartner,
    partnerLabel,
    handleTogglePartner,
    handleMaritalStatusChange,
    handlePartnerMaritalStatusChange,
    onSubmit: form.handleSubmit(data => mutation.mutate(data)),
    isValid: form.formState.isValid,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    data: mutation.data,
    reset: mutation.reset,
  };
}
